import { useCallback, useRef, useState } from 'react';
import type { Control, FieldValues, Path } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { TextInput as RNTextInput } from 'react-native';
import { View } from 'react-native';
import { KeyboardController } from 'react-native-keyboard-controller';
import { HelperText, PaperProvider, TextInput } from 'react-native-paper';
import type { DatePickerModalRangeProps } from 'react-native-paper-dates';
import { DatePickerModal, TimePickerModal } from 'react-native-paper-dates';

import { TodayIcon } from '@/assets';
import {
  DATE_FORMAT,
  TIME_FORMAT,
  DATE_TIME_FORMAT
} from '@/features/post-editor/constants/dateTime';
import { useAppSettingStore } from '@/features/profile-setting/store/useAppSettingStore';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { DateMode } from '@/shared/types/form';
import { getFormattedDate } from '@/shared/utils/date';
import { isIOS } from '@/shared/utils/platform';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

type Props<T extends FieldValues> = {
  name: Path<T>;
  control: Control<T>;
  label: string;
  placeholder: string;
  helperText?: string;
  mode: DateMode;
  validRange?: DatePickerModalRangeProps['validRange'];
};

export const FormDatePicker = <T extends FieldValues>({
  name,
  control,
  label,
  placeholder,
  helperText,
  mode,
  validRange
}: Props<T>) => {
  const { t } = useTranslation(LocalizationNameSpace.SHARED, {
    keyPrefix: 'forms.date'
  });
  const locale = useAppSettingStore((s) => s.selectedLocale);

  const theme = useAppTheme();
  // See: https://github.com/web-ridge/react-native-paper-dates/issues/192#issuecomment-1311912588
  const datetimePickerTheme = {
    ...theme,
    colors: {
      ...theme.colors,
      secondaryContainer: theme.colors.primary
    }
  };

  const formatDateTime = (timestamp: number) => {
    const date = new Date(timestamp);
    switch (mode) {
      case DateMode.DATE: {
        return getFormattedDate(date, DATE_FORMAT);
      }
      case DateMode.TIME: {
        return `${getFormattedDate(date, TIME_FORMAT)} ${t('hour')}`;
      }
      case DateMode.DATE_TIME: {
        return `${getFormattedDate(date, DATE_TIME_FORMAT)} ${t('hour')}`;
      }
    }
  };
  const inputRef = useRef<RNTextInput>(null);

  const [showDatePicker, setShowDatePicker] = useState<boolean>(false);
  const [showTimePicker, setShowTimePicker] = useState<boolean>(false);

  const handleShowPicker = useCallback(async () => {
    inputRef.current?.blur();
    await KeyboardController.dismiss();

    if (mode === DateMode.TIME) {
      setShowTimePicker(true);
    } else {
      setShowDatePicker(true);
    }
  }, [mode]);

  const handleHidePicker = useCallback(() => {
    setShowDatePicker(false);
    setShowTimePicker(false);
  }, []);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange }, fieldState: { error } }) => {
        const dateValue = new Date(value);

        const handleDateConfirm = ({ date }: { date?: Date }) => {
          if (!date) return;

          if (mode === DateMode.DATE_TIME) {
            setShowDatePicker(false);
            setShowTimePicker(true);
          } else {
            const changedDate = new Date(date);
            changedDate.setHours(
              dateValue.getHours(),
              dateValue.getMinutes(),
              0,
              0
            );
            onChange(changedDate.getTime());
            handleHidePicker();
          }
        };

        const handleTimeConfirm = ({
          hours,
          minutes
        }: {
          hours: number;
          minutes: number;
        }) => {
          dateValue.setHours(hours, minutes, 0, 0);
          onChange(dateValue.getTime());
          handleHidePicker();
        };

        return (
          <View>
            <TextInput
              ref={inputRef}
              label={label}
              accessibilityLabel={label}
              accessibilityHint={placeholder}
              showSoftInputOnFocus={false}
              caretHidden
              value={formatDateTime(value)}
              mode="outlined"
              error={!!error}
              style={{ backgroundColor: 'transparent' }}
              right={
                <TextInput.Icon
                  icon={({ color, size }) => (
                    <TodayIcon width={size} height={size} fill={color} />
                  )}
                  onPress={() => {
                    handleShowPicker();
                  }}
                />
              }
              onPress={isIOS ? handleShowPicker : undefined}
              onFocus={isIOS ? undefined : handleShowPicker}
            />
            {(mode === DateMode.DATE || mode === DateMode.DATE_TIME) && (
              <DatePickerModal
                locale={locale}
                mode="single"
                label={label}
                visible={showDatePicker}
                onDismiss={handleHidePicker}
                date={dateValue}
                onConfirm={handleDateConfirm}
                startWeekOnMonday
                validRange={validRange}
              />
            )}

            {(mode === DateMode.TIME || mode === DateMode.DATE_TIME) && (
              <PaperProvider theme={datetimePickerTheme}>
                <TimePickerModal
                  locale={locale}
                  label={label}
                  visible={showTimePicker}
                  cancelLabel={t('cancel')}
                  onDismiss={handleHidePicker}
                  onConfirm={handleTimeConfirm}
                  hours={dateValue.getHours()}
                  minutes={dateValue.getMinutes()}
                />
              </PaperProvider>
            )}
            {isEmptyOrNull(error?.message) ? (
              !isEmptyOrNull(helperText) && (
                <HelperText type="info">{helperText}</HelperText>
              )
            ) : (
              <HelperText type="error">{error?.message}</HelperText>
            )}
          </View>
        );
      }}
    />
  );
};
